import { ReactElement, memo, useCallback, useState } from "react"
import styled from "styled-components"

import ApiEventTypes from "api/ApiEventTypes"
import { useEffect } from "react"
import { setEventType } from "reducers/event-types/event-types-actions"

import { CellActionButtons } from "components/EvtKit/list-items/CellActionButtons"
import { CellLogo } from "components/EvtKit/list-items/CellLogo"
import { CellSwitch } from "components/EvtKit/list-items/CellSwitch"
import { CellText } from "components/EvtKit/list-items/CellText"
import Spinner from "components/EvtKit/loading-spinner/LoadingSpinner"
import { changeModalState } from "reducers/event-types/event-types-actions"
import { useAppDispatch } from "reducers/store"
import { DO_NOT_HAVE_FREE_TRIAL, eventTypesMapper } from "utils/mapper/event-types"
import UnpublishedEventTypeModal from "./component/UnpublishedEventTypeModal"

interface ContainerProps {
  align?: string
  direction?: string
  justify?: string
}

const Container = styled.div<ContainerProps>`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-height: 100%;
  overflow-y: auto;
  gap: 16px;
  margin-top: 24px;
  padding-right: 24px;
`

const ListItemsContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  gap: 16px;
  padding-right: 24px;
`

const ListItemsContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px;
  border-radius: 8px;
  background: #fff;
  background-color: ${(props) => props.theme.palette.white};
  box-shadow: 0px 2px 6px 0px rgba(13, 10, 44, 0.08);
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
`

interface Props {
  hasData: boolean
  filterValue: string
  setHasData: (value: boolean) => void
}

const EventTypesTable = (props: Props): ReactElement => {
  const { hasData, filterValue, setHasData } = props
  const [data, setData] = useState<App.EventTypeMapper[]>([])
  const [loading, setLoading] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [selectedItem, setSelectedItem] = useState<App.EventTypeMapper | null>(null)

  const appDispatch = useAppDispatch()

  const handleEventTypeContext = useCallback((item) => {
    const { freeTrial } = item
    const hasFreeTrial = freeTrial !== DO_NOT_HAVE_FREE_TRIAL

    appDispatch(
      setEventType({
        ...item,
        freeTrial: hasFreeTrial
      })
    )
  }, [])

  const handleFilter = useCallback(async () => {
    if (filterValue) {
      const { events } = await new ApiEventTypes().filterByProduct(filterValue)

      return events
    }
  }, [filterValue])

  const fetchData = useCallback(async () => {
    if (!filterValue) setLoading(true)

    if (filterValue) {
      setLoading(false)
      const filteredEvents = await handleFilter()
      const formatedFilteredEvents = eventTypesMapper(filteredEvents)

      setHasData(formatedFilteredEvents.length > 0)

      return setData(formatedFilteredEvents)
    }
    const { events } = await new ApiEventTypes().getAll()

    const formatedEvents = eventTypesMapper(events)

    setLoading(false)
    setHasData(formatedEvents.length > 0)

    return setData(formatedEvents)
  }, [filterValue])

  const onClickEdit = useCallback((item: any) => {
    appDispatch(changeModalState(true, "update"))
    handleEventTypeContext(item)
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleSwitchClick = useCallback((item: App.EventTypeMapper) => {
    setSelectedItem(item)
    setShowConfirmModal(true)
  }, [])

  const confirmSwitchClick = useCallback(async () => {
    setLoading(true)
    if (selectedItem?.id) {
      await new ApiEventTypes().updateEventTypeIsPublished(selectedItem.id)
      fetchData()
      setShowConfirmModal(false)
      setSelectedItem(null)
    }
    setLoading(false)
  }, [selectedItem, fetchData])

  return (
    <Container align="flex-start">
      <Content>
        {hasData && (
          <ListItemsContainer>
            {loading && <Spinner />}
            {!loading &&
              data.map((item, index) => (
                <ListItemsContent key={index}>
                  <CellLogo title={item.name} />
                  <CellText title="Chave ID" content={item.key} />
                  <CellText title="Preço por uso" content={`${item.price}`} />
                  <CellText title="Free trial" content={item.freeTrial} />
                  <CellText title="Produto" content={item.product} />
                  <CellSwitch initialState={item.isPublished} onClickSwitch={() => handleSwitchClick(item)} />
                  <CellActionButtons onClickEdit={() => onClickEdit(item)} />
                </ListItemsContent>
              ))}
          </ListItemsContainer>
        )}
        {showConfirmModal && selectedItem && (
          <UnpublishedEventTypeModal
            planData={selectedItem}
            onConfirm={confirmSwitchClick}
            onCancel={() => setShowConfirmModal(false)}
            loading={loading}
          />
        )}
      </Content>
    </Container>
  )
}

export default memo(EventTypesTable)
